<template>
    <div
        v-if="isVisible"
        class="w-100"
    >
        <h2
            v-if="text.title"
            :class="['h2 text-center', {'mb-16': text.descr, 'mb-48': !text.descr}, {'text-left': textLeft}]"
        >
            {{ text.title }}
        </h2>
        <p
            v-if="text.descr"
            class="text text-center text-light mb-48"
            v-html="text.descr"
        />
        <template v-if="!hasFormSend">
            <ValidationObserver
                v-slot="{ handleSubmit }"
                ref="callbackFormRef"
                tag="div"
                class="callback-form"
            >
                <form
                    :class="['callback-form__form', {'column': column}, {'start': start}]"
                    @submit.prevent="handleSubmit(submitForm)"
                >
                    <TextInput
                        v-model="model.callbackFormName"
                        class="form-input grey"
                        label="Имя"
                        name="callbackFormName"
                        rules="required|onlyRuLetters"
                    />
                    <NumberInput
                        v-model="model.callbackFormPhone"
                        :mask="'+7 (\\999) 999-99-99'"
                        class="form-input grey"
                        label="Номер телефона"
                        name="callbackFormPhone"
                        rules="required|phone"
                    />
                    <UIButton
                        class="submit-btn"
                        type="submit"
                        :label="buttonLabel"
                        :size="column && 'lg'"
                    />
                </form>
            </ValidationObserver>
        </template>
        <template v-else>
            <FormSubmittingMessage
                :message="text.submitingMessage"
            />
        </template>
    </div>
</template>

<script>
import {defineComponent, ref, shallowRef, onMounted} from 'vue';
import {useRoute, useContext} from '@nuxtjs/composition-api';
import {UIButton} from '@cibsite/ui-components-vue2';

import {useToastsStore} from '@/stores/toasts';
import {useLoaderStore} from '@/stores/loader';
import {useMetrica} from '@/core/composables/useMetrica';
import {PATH_SCHEME} from '@/core/constants/callbackFormIds';
import CONSTANTS from '@/core/constants';
import {checkWorkTime} from '@/core/utils/checkWorkTime';
import toCamelCase from '@/core/utils/toCamelCase';

import TextInput from '@/components/UI-components/FormFields/TextInput';
import NumberInput from '@/components/UI-components/FormFields/NumberInput';

export default defineComponent({
    name: 'CallbackForm',
    components: {
        FormSubmittingMessage: () => import('@/components/Forms/FormSubmittingMessage.vue'),
        UIButton,
        TextInput,
        NumberInput,
    },
    props: {
        callCenterLabel: {
            type: String,
            default: () => '',
        },
        id: {
            type: String,
            default: () => '',
        },
        column: {
            type: Boolean,
            default: () => false,
        },
        start: {
            type: Boolean,
            default: () => false,
        },
        textLeft: {
            type: Boolean,
            default: () => false,
        },
        text: {
            type: Object,
            default: () => ({
                title: 'Остались вопросы?',
                descr: 'Если у&nbsp;вас возникли вопросы, оставьте свой номер и&nbsp;мы&nbsp;перезвоним вам',
                submitingMessage: `
                    Ваша заявка принята, наши специалисты свяжутся с&nbsp;вами в&nbsp;ближайшее время.
                `,
            }),
        },
        buttonLabel: {
            type: String,
            default: () => 'Заказать звонок',
        },
        endpoint: {
            type: String,
            default: () => '',
        },
        roundTheClock: {
            type: Boolean,
            default: () => false,
        },
        sendLead: {
            type: Boolean,
            default: () => true,
        },
    },
    setup(props, context) {
        const {$api, $fetchFeedbackLead} = useContext();
        const route = useRoute();

        const loader = useLoaderStore();
        const toastsStore = useToastsStore();
        const {error: toastedError} = toastsStore;
        const {sendStatistics} = useMetrica();

        const callbackFormRef = shallowRef(null);
        const model = ref({
            callbackFormName: '',
            callbackFormPhone: '',
        });
        const hasFormSend = ref(false);
        const formId = ref('');
        const isVisible = ref(false);

        function getFormId() {
            formId.value = PATH_SCHEME.find((item) => item.path === route.value.path)?.id ?? toCamelCase(props.id);
        }

        async function submitForm() {
            const payload = {
                name: model.value.callbackFormName,
                phone: model.value.callbackFormPhone,
                callCenterLabel: props.callCenterLabel?.length ? props.callCenterLabel?.trim() : formId.value,
                link: route.value.path,
                formType: formId.value, // Нужно для лидов
            };

            try {
                loader.show();
                const {data} = await $api.forms.callback.send(payload, {url: props.endpoint});
                model.value.callbackFormName = '';
                model.value.callbackFormPhone = '';
                hasFormSend.value = true;
                callbackFormRef.value.reset();

                if (props.sendLead) {
                    $fetchFeedbackLead(payload);
                }

                context.emit('onSuccess', data);

                if (!props.endpoint.length) {
                    sendStatistics();
                }
            } catch (error) {
                console.error(error);
                toastedError(error?.response?.data?.error ?? 'Ошибка');
            } finally {
                loader.hide();
            }
        }

        onMounted(() => {
            getFormId();
            isVisible.value = props.roundTheClock || checkWorkTime(CONSTANTS.WORKED_TIME.CALL_CENTER_WORKED_TIME);
        });

        return {
            callbackFormRef,
            model,
            hasFormSend,
            formId,
            isVisible,
            submitForm,
        };
    },
});
</script>

<style lang="scss">
.h2 {
    &.text-left {
        text-align: left;
    }
}

.callback-form {
    &__form {
        display: flex;
        align-items: flex-start;

        .form-input {
            max-width: 400px;
            margin-right: 24px;
        }

        &.column {
            flex-direction: column;
            align-items: center;

            .form-input {
                max-width: 588px;
                margin-right: 0;
            }

            @media screen and (max-width: $tablet) {
                padding: 0 16px;

                .form-input {
                    width: 100%;
                    max-width: unset;
                }

                .submit-btn {
                    margin: 0 auto;
                    width: initial;
                }
            }

            @media screen and (max-width: $mobile) {
                padding: 0;
            }
        }

        &.start {
            align-items: flex-start;
        }

        @media screen and (max-width: $tablet) {
            margin-top: 40px;
            flex-direction: column;

            .form-input {
                width: 100%;
                max-width: unset;
            }

            .submit-btn {
                max-width: 320px;
                width: 100%;
                margin: 0 auto;
            }
        }
    }

    .text-input.grey .field-input,
    .text-input.grey .field-area {
        background-color: #f1f3f9;
    }
}
</style>

<style lang="scss" scoped>
.text {
    font-size: 16px;
}
</style>
